import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import BOLayout from '@/pages/back-office/BOLayout';

const PromoForm = () => {
  const { restaurantId, promoId } = useParams(); // Récupération des paramètres URL
  const navigate = useNavigate();

  // Initialisation du state
  const [promoData, setPromoData] = useState({
    reference: '',
    discountType: 'percentage',
    discountValue: 0,
    minOrderValue: 0,
    startDate: '',
    endDate: '',
    active: true,
  });

  const isEditing = Boolean(promoId); // Mode édition si promoId est présent
  // Helper function to convert date safely
  const convertDateForInput = (date) => {
    if (!date) return '';
    if (typeof date === 'string') {
      return date.split('T')[0];
    }
    if (date._seconds) {
      return new Date(date._seconds * 1000).toISOString().split('T')[0];
    }
    if (date.toDate && typeof date.toDate === 'function') {
      return date.toDate().toISOString().split('T')[0];
    }
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    return String(date).split('T')[0];
  };

  // Requête API pour charger les données du code promo si en mode édition
  useEffect(() => {
    if (isEditing) {
      axios
        .get(`${import.meta.env.VITE_API_URL}/api/restaurants/${restaurantId}/promos/${promoId}`)
        .then((response) => {
          if (response.data) {
            const data = response.data;
            setPromoData({
              ...data,
              startDate: convertDateForInput(data.startDate),
              endDate: convertDateForInput(data.endDate),
            });
          } else {
            toast.error('Les données du code promo sont introuvables');
          }
        })
        .catch((error) => {
          toast.error('Erreur lors du chargement du code promo');
        });
    }
  }, [isEditing, promoId, restaurantId]);

  // Gestion des changements dans les champs du formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setPromoData({
      ...promoData,
      [name]: value,
    });
  };

  // Gestion de la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (isEditing) {
        // Mise à jour du code promo existant
        await axios.put(
          `${import.meta.env.VITE_API_URL}/api/restaurants/${restaurantId}/promos/${promoId}`,
          promoData
        );
        toast.success('Code promo mis à jour avec succès');
      } else {
        // Création d'un nouveau code promo
        await axios.post(
          `${import.meta.env.VITE_API_URL}/api/restaurants/${restaurantId}/promos`,
          promoData
        );
        toast.success('Code promo créé avec succès');
      }
      navigate(`/back-office/restaurants/${restaurantId}/promos`);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde du code promo');
    }
  };

  return (
    <BOLayout>
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">
          {isEditing ? 'Modifier le code promo' : 'Créer un nouveau code promo'}
        </h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Référence du code promo */}
          <div>
            <label className="block font-medium">Référence du code promo</label>
            <input
              type="text"
              name="reference"
              value={promoData.reference.trim().toUpperCase()}
              onChange={handleChange}
              className="w-full border p-2 rounded"
              required
            />
          </div>

          {/* Type de réduction */}
          <div>
            <label className="block font-medium">Type de réduction</label>
            <select
              name="discountType"
              value={promoData.discountType}
              onChange={handleChange}
              className="w-full border p-2 rounded"
            >
              <option value="percentage">Pourcentage</option>
              <option value="fixed">Montant fixe (€)</option>
              <option value="free">Commande gratuite</option>
            </select>
          </div>

          {/* Valeur de la réduction */}
          <div>
            <label className="block font-medium">Valeur de la réduction</label>
            <input
              type="number"
              name="discountValue"
              value={promoData.discountValue}
              onChange={handleChange}
              className="w-full border p-2 rounded"
              min={0}
              max={promoData.discountType === 'percentage' ? 100 : undefined}
              required
            />
          </div>

          {/* Montant minimum de commande */}
          <div>
            <label className="block font-medium">Montant minimum de commande</label>
            <input
              type="number"
              name="minOrderValue"
              value={promoData.minOrderValue}
              onChange={handleChange}
              className="w-full border p-2 rounded"
              min={0}
              required
            />
          </div>

          {/* Date de début */}
          <div>
            <label className="block font-medium">Date de début</label>
            <input
              type="date"
              name="startDate"
              value={promoData.startDate}
              onChange={handleChange}
              min={!isEditing ? new Date().toISOString().split('T')[0] : ''}
              className="w-full border p-2 rounded"
              required
            />
          </div>

          {/* Date de fin */}
          <div>
            <label className="block font-medium">Date de fin (facultatif)</label>
            <input
              type="date"
              name="endDate"
              value={promoData.endDate || ''}
              onChange={handleChange}
              min={new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0]}
              className="w-full border p-2 rounded"
            />
          </div>

          {/* Statut actif/inactif */}
          <div className="flex items-center">
            <label className="block font-medium mr-4">Activer le code promo</label>
            <input
              type="checkbox"
              name="active"
              checked={promoData.active}
              onChange={(e) => setPromoData({ ...promoData, active: e.target.checked })}
              className="w-6 h-6"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              {isEditing ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </BOLayout>
  );
};

export default PromoForm;
